import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Spinner } from '@/components/ui';
import { copyText } from '@/lib/utils/clipboard';
import { usePatients } from '../hooks/usePatients';
import type { Patient } from '../types/patient.types';

interface AttachCodeDialogProps {
  open: boolean;
  /** The account holder (primary patient) whose `actCode` is shown. */
  patient: Patient | null;
  onClose: () => void;
}

const CopyIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M5 15V6a2 2 0 0 1 2-2h9" />
  </svg>
);

const CheckIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m5 12 5 5L20 7" />
  </svg>
);

const RULE_KEYS = ['single_person', 'access', 'single_use', 'rotates'] as const;

/**
 * "Cod atașare" — shows the account holder's one-time attach code (`ActCode`
 * from GetPatient) together with the rules of use and a copy action. The code
 * rotates server-side once somebody uses it, so the patient list is refetched
 * every time the dialog opens to make sure the current code is displayed.
 */
export function AttachCodeDialog({ open, patient, onClose }: AttachCodeDialogProps) {
  const { t } = useTranslation();
  // `refetch` is referentially stable in TanStack Query v5, so it's safe as a dep.
  const { refetch, isFetching } = usePatients();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCopied(false);
    void refetch();
  }, [open, refetch]);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const code = patient?.actCode ?? null;

  const copy = async () => {
    if (!code) return;
    const ok = await copyText(code);
    setCopied(ok);
  };

  return (
    <Dialog
      open={open}
      title={t('patients.attachCode.title')}
      onClose={onClose}
      footer={
        <Button variant="primary" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      {/* Code panel */}
      <div className="rounded-card bg-muted-100 px-4 py-5 text-center">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-mute">
          {t('patients.attachCode.code_label')}
        </p>
        {code ? (
          <>
            <p
              className="mt-2 select-all break-all font-mono text-3xl font-bold tracking-[0.2em] text-primary"
              data-testid="attach-code"
            >
              {code}
            </p>
            <div className="mt-4 flex items-center justify-center gap-3">
              <Button variant="outline" size="sm" onClick={() => void copy()}>
                {copied ? CheckIcon : CopyIcon}
                {copied ? t('patients.attachCode.copied') : t('patients.attachCode.copy')}
              </Button>
              {isFetching && (
                <span className="inline-flex items-center gap-1.5 text-xs text-ink-mute">
                  <Spinner size={12} />
                  {t('patients.attachCode.refreshing')}
                </span>
              )}
            </div>
          </>
        ) : (
          <p className="mt-2 text-sm text-ink-soft">
            {isFetching ? t('common.loading') : t('patients.attachCode.unavailable')}
          </p>
        )}
      </div>

      {/* Rules of use */}
      <ul className="mt-5 flex flex-col gap-3">
        {RULE_KEYS.map((key, index) => (
          <li key={key} className="flex gap-3 text-sm text-ink-soft">
            <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary-light text-[11px] font-bold text-primary">
              {index + 1}
            </span>
            <span>{t(`patients.attachCode.rules.${key}`)}</span>
          </li>
        ))}
      </ul>

      <p className="mt-5 rounded-input bg-warning-light px-3.5 py-2.5 text-sm font-medium text-[#9a6700]">
        {t('patients.attachCode.caution')}
      </p>
    </Dialog>
  );
}
